import { useCallback, useLayoutEffect, useRef } from "react";
import { Platform } from "react-native";
import EventSource, { type CustomEvent } from "react-native-sse";

import { useAuth } from "@/features/auth";
import { QueryRequest } from "@/features/knowledge-graph/types";
import { useToastNotification } from "@/hooks/useToastNotification";
import { getFreshAccessToken } from "@/providers/accessTokenSource";

import { env } from "@/lib/env";

/**
 * Custom SSE event types used by the AI generation endpoint.
 */
type CustomSSEEvents = "start" | "status" | "token" | "end";

/**
 * Parsed token event data structure.
 */
interface TokenEventData {
  /** Event type discriminator */
  readonly type: "token";
  /** Text content of the token */
  readonly content: string;
}

/**
 * Metadata about a generation request
 * Contains information about the request context that should be preserved
 * through the entire generation lifecycle (including retries)
 */
export interface GenerationMetadata {
  /**
   * Language code for the generation (e.g., "en_US", "es_ES")
   */
  language: string;
  /**
   * ID of the player the summary is being generated for
   */
  playerId: string;
}

/**
 * Props for the useSSEGeneration hook.
 * Provides callbacks for content updates, completion, error handling, and state changes.
 */
interface UseSSEGenerationProps {
  onContentUpdate: (content: string) => void;
  onComplete: (finalContent: string, metadata: GenerationMetadata) => void;
  onError: (error: string) => void;
  onGeneratingChange: (isGenerating: boolean) => void;
}

/**
 * Hook for managing Server-Sent Events (SSE) based AI content generation.
 *
 * Provides a simplified interface for streaming AI-generated content via SSE. The hook
 * trusts the backend to send proper completion events ([DONE]) and relies on the
 * react-native-sse library's built-in error handling and reconnection capabilities.
 * @param props - Configuration object containing event handlers
 * @param props.onContentUpdate - Callback invoked with updated content as tokens stream in
 * @param props.onComplete - Callback invoked when generation completes with final content and metadata
 * @param props.onError - Callback invoked when an error occurs during generation
 * @param props.onGeneratingChange - Callback invoked when generation state changes (true when starting, false when done)
 * @returns Object with generateWithSSE and disconnect functions
 * @remarks
 * Key features:
 * - Real-time content streaming via Server-Sent Events
 * - Automatic retry logic with exponential backoff (1s, 2s, 4s up to 3 attempts)
 * - Mobile-specific error handling with clear user messages (no retries on network errors)
 * - Backend [DONE] event handling for completion detection (trusts backend timing)
 * - Metadata preservation across retries (language, playerId)
 * - Proper cleanup on unmount to prevent state updates after unmount
 *
 * Trust-based architecture:
 * - Backend [DONE] events: Trusts backend to send [DONE] when generation completes
 * - Library functionality: Uses react-native-sse's pollingInterval (set to 0 for no automatic polling)
 * - No safety timeouts: Avoids race conditions between client timeouts and backend completion
 * - EventSource error handling: Leverages library's built-in error detection and recovery
 *
 * Completion flow:
 * 1. `generateWithSSE()` called with request and metadata
 * 2. Sets `isGenerating` to true via `onGeneratingChange(true)`
 * 3. Opens SSE connection to backend
 * 4. Streams tokens, calling `onContentUpdate()` with accumulated content
 * 5. Backend sends [DONE] event when complete
 * 6. Calls `onComplete()` with final content and original metadata
 * 7. Container sets `isGenerating` to false after rendering content
 *
 * Error handling:
 * - Network errors on mobile: Shows clear message, no retry (user needs to fix connection)
 * - Other errors: Retries up to 3 times with exponential backoff
 * - After max retries: Calls `onError()` and shows toast notification
 *
 * Why no client-side timeouts?
 * Previous implementation used a 30-second safety timeout, but this created race conditions:
 * - Timeout fires just before [DONE] arrives → user sees error, then content appears
 * - Timeout fires after [DONE] but before callback completes → partial state update
 * - Different network speeds meant timeout was either too short or unnecessarily long
 * The chat refactor project (refactor-chat) demonstrated that trusting backend events
 * and library error handling is more reliable than client-side timeout workarounds.
 * @example
 * ```tsx
 * const { generateWithSSE, disconnect } = useSSEGeneration({
 *   onContentUpdate: (content) => setAiContent(content),
 *   onComplete: (finalContent, metadata) => handleComplete(finalContent, metadata),
 *   onError: (error) => showError(error),
 *   onGeneratingChange: (isGenerating) => setLoading(isGenerating),
 * });
 *
 * // Start generation
 * generateWithSSE(
 *   { question: "Generate summary", sessionId: "session-123" },
 *   { language: "en_US", playerId: "player-456" }
 * );
 *
 * // Cleanup on unmount
 * useEffect(() => disconnect, [disconnect]);
 * ```
 */
export const useSSEGeneration = ({
  onContentUpdate,
  onComplete,
  onError,
  onGeneratingChange,
}: UseSSEGenerationProps) => {
  const retryCountRef = useRef(0);
  const metadataRef = useRef<GenerationMetadata | null>(null);
  const doneReceivedRef = useRef(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  /** Bumped per dispatch and on disconnect, so a stale token read never dials (SE-8687). */
  const dispatchIdRef = useRef(0);
  const generateWithSSERef = useRef<
    (
      request: QueryRequest,
      metadata: GenerationMetadata,
      retryAttempt?: number
    ) => void
  >(() => {
    // Placeholder - will be updated after generateWithSSE is defined
  });
  const maxRetries = 3;
  const toast = useToastNotification();
  const auth = useAuth();

  // Helper to cleanup loading states
  const cleanupStates = useCallback(() => {
    onGeneratingChange(false);
  }, [onGeneratingChange]);

  /**
   * Opens the stream for one attempt with an already-resolved token.
   * @param request - Query request to post
   * @param retryAttempt - Zero-based attempt number, for the retry budget
   * @param accessToken - Token read at dial time (SE-8687), or undefined
   */
  const openStream = useCallback(
    (
      request: QueryRequest,
      retryAttempt: number,
      accessToken: string | undefined
    ) => {
      try {
        // Add accessKey to request if auth token is available
        const requestWithAuth = {
          ...request,
          ...(accessToken && { accessKey: accessToken }),
        };

        // eslint-disable-next-line functional/no-let -- Required for accumulating SSE responses
        let fullResponse = "";

        const sseEndpoint = env.EXPO_PUBLIC_SSE_ENDPOINT || "";
        const headers = {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          "X-Supports-Status": "true",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        };

        const body = JSON.stringify({
          ...requestWithAuth,
          isStreaming: true,
        });

        // Create EventSource with event handlers with mobile-specific error handling
        const eventSource = new EventSource<CustomSSEEvents>(sseEndpoint, {
          method: "POST",
          headers,
          body,
          pollingInterval: 0,
        });

        // Store event source ref for cleanup
        eventSourceRef.current = eventSource;

        // Handle open event
        eventSource.addEventListener("open", () => {
          // Connection opened
        });

        // Handle start event (backend initialization)
        eventSource.addEventListener("start", () => {
          // Start event received, generation beginning
        });

        // Handle status events (backend progress updates - can be ignored)
        eventSource.addEventListener("status", () => {
          // Status events are informational only, no action needed
        });

        // Handle token events (streamed content from backend)
        eventSource.addEventListener("token", (event: CustomEvent<"token">) => {
          if (!event.data) {
            return;
          }

          try {
            const parsed = JSON.parse(event.data) as TokenEventData;
            if (parsed.type === "token" && parsed.content) {
              fullResponse += parsed.content;
              onContentUpdate(fullResponse);
            }
          } catch {
            // Ignore unparseable token messages
          }
        });

        // Handle end event (backend signals completion)
        eventSource.addEventListener("end", () => {
          // End event received, backend finished generating
        });

        // Handle message events (for [DONE] signal)
        eventSource.addEventListener("message", event => {
          const data = event.data as string;

          // Handle [DONE] message
          if (data === "[DONE]") {
            // Mark that [DONE] has been received
            doneReceivedRef.current = true;

            if (fullResponse.trim()) {
              if (metadataRef.current) {
                onComplete(fullResponse, metadataRef.current);
              } else {
                onError(
                  "Internal error: missing generation metadata. Please try again."
                );
              }
            }

            // Note: cleanupStates is now called by the Container after content is rendered
            // This ensures spinner stays visible until text is actually on screen
            // Disconnect SSE after successful completion
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            return;
          }

          try {
            const parsed = JSON.parse(data);
            if (parsed.type === "token" && parsed.content) {
              fullResponse += parsed.content;
              onContentUpdate(fullResponse);
            }
          } catch {
            // Ignore unparseable messages
          }
        });

        // Handle error events
        eventSource.addEventListener("error", event => {
          const eventErrorMessage =
            event.type === "exception"
              ? (event as { error: Error }).error.message
              : event.type === "timeout"
                ? "Connection timeout"
                : "SSE connection error";
          const error = new Error(eventErrorMessage);

          // On mobile, provide more specific error messages
          const isMobile = Platform.OS === "ios" || Platform.OS === "android";
          const errorMessage = isMobile
            ? "Failed to connect to AI service. Please check your internet connection and try again."
            : "Failed to generate summary. Please try again.";

          if (metadataRef.current) {
            // Don't retry on mobile network errors - fail immediately with clear message
            if (
              isMobile &&
              (error.message.includes("Network") ||
                error.message.includes("timeout") ||
                error.message.includes("connection"))
            ) {
              onError(errorMessage);
              cleanupStates();
              toast.show({
                action: "danger",
                description: errorMessage,
              });
            } else if (retryAttempt < maxRetries) {
              // Retry with exponential backoff: 1s, 2s, 4s
              const retryDelay = Math.pow(2, retryAttempt) * 1000;
              retryCountRef.current = retryAttempt + 1;
              setTimeout(() => {
                generateWithSSERef.current(
                  request,
                  metadataRef.current!,
                  retryAttempt + 1
                );
              }, retryDelay);
            } else {
              onError("Failed to generate summary. Please try again.");
              cleanupStates();
              toast.show({
                action: "danger",
                description: "Failed to generate AI summary",
              });
            }
          } else {
            onError(errorMessage);
            cleanupStates();
          }
        });

        // Handle close events
        eventSource.addEventListener("close", () => {
          // Only cleanup if [DONE] was not received (error case)
          // If [DONE] was received, cleanup already happened in onMessage
          if (!doneReceivedRef.current) {
            cleanupStates();
          }
        });
      } catch (_err) {
        const isMobile = Platform.OS === "ios" || Platform.OS === "android";

        // On mobile, provide better error messaging and don't retry
        if (isMobile) {
          const errorMsg =
            "An error occurred while generating the summary. Please try again.";
          onError(errorMsg);
          cleanupStates();
          toast.show({
            action: "danger",
            description: errorMsg,
          });
        } else if (metadataRef.current && retryAttempt < maxRetries) {
          // Retry with exponential backoff: 1s, 2s, 4s
          const retryDelay = Math.pow(2, retryAttempt) * 1000;
          retryCountRef.current = retryAttempt + 1;
          setTimeout(() => {
            generateWithSSERef.current(
              request,
              metadataRef.current!,
              retryAttempt + 1
            );
          }, retryDelay);
        } else if (metadataRef.current) {
          // Max retries exceeded
          onError("Failed to generate summary. Please try again.");
          cleanupStates();
          toast.show({
            action: "danger",
            description: "Failed to generate AI summary",
          });
        } else {
          onError("Internal error: missing generation metadata.");
          cleanupStates();
        }
      }
    },
    [onError, cleanupStates, onComplete, onContentUpdate, toast]
  );

  const generateWithSSE = useCallback(
    (
      request: QueryRequest,
      metadata: GenerationMetadata,
      retryAttempt: number = 0
    ) => {
      // Store metadata for this generation
      metadataRef.current = metadata;

      // Reset [DONE] flag at start of generation
      doneReceivedRef.current = false;

      onGeneratingChange(true);

      // Only clear error on first attempt, not retries
      if (retryAttempt === 0) {
        onError("");
        retryCountRef.current = 0;
      }

      // SE-8687: read the token at dial time, refreshing it if it has expired.
      // The render-time `auth` value is whatever the last GraphQL request left
      // behind, which a long stay on one screen lets expire. A disconnect or a
      // newer generation while the read is in flight supersedes this one.
      dispatchIdRef.current += 1;
      const dispatchId = dispatchIdRef.current;
      void getFreshAccessToken(auth?.data?.accessToken).then(accessToken => {
        if (dispatchIdRef.current === dispatchId) {
          openStream(request, retryAttempt, accessToken);
        }
      });
    },
    [auth, onGeneratingChange, onError, openStream]
  );

  /**
   * Disconnect the active SSE connection and cleanup resources
   * Should be called on component unmount to prevent state updates after unmount
   */
  const disconnect = useCallback(() => {
    dispatchIdRef.current += 1;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Keep the ref updated with the latest generateWithSSE function
  useLayoutEffect(() => {
    generateWithSSERef.current = generateWithSSE;
  }, [generateWithSSE]);

  return { generateWithSSE, disconnect };
};
